import clsx from 'clsx';
import type { DragEvent, ChangeEvent } from 'react';
import { useRef, useCallback, useEffect, useState } from 'react';

import { Icon } from '@flintmint/web-shared/components/icon';
import { Input } from '@flintmint/web-shared/components/input';
import { Button } from '@flintmint/web-shared/components/button';
import { VoiceRecorder } from '@/components/voice-recorder/voice-recorder';

import { isValidUrl } from '@/utils/helper-utils';
import { hasFocusedInput } from '@flintmint/web-shared/utils/ui';
import { getSupportedClipboardFiles, VALID_EXTENSIONS } from '@/utils/file-upload';

import FileGroupSvg from '@/assets/icons/file-group.svg?react';

import styles from './add-context-items.module.scss';

export const AddContextItems = (props: AddItemsViewProps) => {
  const { onFiles, onUrlAdd, onCreateBlankItem } = props;
  const [documentUrl, setDocumentUrl] = useState('');
  const [isDragActive, setIsDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement> | ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      setIsDragActive(false);
      onFiles('dataTransfer' in e ? e.dataTransfer?.files ?? null : e.target?.files ?? null);
    },
    [onFiles]
  );

  const handleUrlAdd = useCallback(() => {
    if (!isValidUrl(documentUrl)) return;
    onUrlAdd(documentUrl);
    setDocumentUrl('');
  }, [documentUrl, onUrlAdd]);

  useEffect(() => {
    const handlePaste = (e: ClipboardEvent) => {
      if (e.defaultPrevented || hasFocusedInput()) return;
      const supportedFiles = getSupportedClipboardFiles(e.clipboardData?.files);

      if (supportedFiles?.length) {
        e.preventDefault();
        onFiles(supportedFiles);
      }
    };

    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, [onFiles]);

  return (
    <div className={styles.addContextItems}>
      <div
        role="region"
        aria-label="File upload drop zone. Click or drag and drop files here"
        className={clsx(styles.dropZone, { [styles.active]: isDragActive })}
        onDrop={handleDrop}
        onClick={() => fileInputRef.current?.click()}
        onDragEnter={(e) => {
          e.preventDefault();
          setIsDragActive(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          if (!e.currentTarget.contains(e.relatedTarget as Node)) setIsDragActive(false);
        }}
        onDragOver={(e) => e.preventDefault()}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={VALID_EXTENSIONS.join(',')}
          onChange={handleDrop}
          data-testid="context-items-file-input"
          className={styles.fileInput}
        />
        <FileGroupSvg />
        <div className={styles.dropMessage}>
          <Button appearance="greyscale" color="dark" cornerRadius="smooth" icon={<Icon type="upload-04" size={20} />}>
            Upload files
          </Button>
          <div className={styles.or}>or</div>
          <h4>Drag & Drop Here</h4>
          <p>Add text files, markdown files, images, voice recordings and PDFs to provide AI with context to work with.</p>
        </div>
      </div>
      <div className={styles.actions}>
        <div className={styles.urlInputHolder}>
          <Input
            type="url"
            size="big"
            gap="medium"
            value={documentUrl}
            appearance="outline"
            className={styles.urlInput}
            placeholder="Enter document URL"
            left={<Icon type="browser" size={20} />}
            onChange={(e) => setDocumentUrl(e.target.value)}
          />
          {isValidUrl(documentUrl) && (
            <Button cornerRadius="smooth" size="big" onClick={handleUrlAdd}>
              Add
            </Button>
          )}
        </div>
        <button className={styles.contextButton} onClick={onCreateBlankItem}>
          <Icon type="text-smallcaps" size={20} />
          Add a blank item
        </button>
        <VoiceRecorder onSave={(file) => onFiles([file])} />
      </div>
    </div>
  );
};

interface AddItemsViewProps {
  onCreateBlankItem: () => void;
  onUrlAdd: (url: string) => void;
  onFiles: (files: FileList | File[] | null) => void;
}
